import {requestGet, requestPost} from "../util/request";
import {
    codeProps,
    nativeProps,
    orderStatusProps,
    productOrderProps,
    productRechargeStatusProps,
    rechargeStatusProps
} from "../types/payment";
import {memberInfoProps, ProductInfoProps} from "../types/member.type";

interface OrderResponse {
    code: number;
    data: nativeProps;
    message: string;
    i18nKey?: string
}

interface MembershipRenewResponse {
    code: number;
    data: {
        out_trade_no: string;
        qr_code: string;
        tier: string;
        renewal: boolean;
        amount: number;
        description: string;
    };
    message: string;
    i18nKey?: string;
}

interface QrCodeResponse {
    code: number;
    data: codeProps;
    message: string;
    i18nKey?: string
}

interface orderStatusResponse {
    code: number;
    data: orderStatusProps;
    message: string;
    i18nKey?: string
}

interface rechargeStatusResponse {
    code: number;
    data: {records: rechargeStatusProps[]};
    message: string;
    i18nKey?: string
}

interface productRechargeStatusResponse {
    code: number;
    data: productRechargeStatusProps;
    message: string;
    i18nKey?: string
}

interface giftResponse {
    code: number;
    data: giftCodeProps;
    message: string;
    i18nKey?: string
}

interface giftCodeProps {
    success: boolean,
    message: string,
    rewardAmount: number,
    pointsType: number,
    currentFlowingWater: number,
    currentMemberDrops: number,
    currentOtherDrops: number,
    i18nkey: string
}

interface createProductsOrderResponse {
    code: number;
    data: productOrderProps;
    message: string;
    i18nKey?: string
}

interface memberResponse {
    code: number;
    data: memberInfoProps;
    message: string;
    i18nKey?: string
}

interface productResponse {
    code: number;
    data: ProductInfoProps;
    message: string;
    i18nKey?: string
}

export interface InvoiceRecordItem {
    out_trade_no: string;
    source_type: string;
    payment_time: string;
    payment_amount: string;
    payment_description: string;
    consume_description: string;
    transaction_id: string;
    invoice_status: string;
    invoice_status_text: string;
    payment_status: string;
    pdf_filename?: string;
    reject_reason?: string
    pdf_url?: string
    apply_time?: string
}

interface InvoiceRecordsResponse {
    code: number;
    data: {
        records: InvoiceRecordItem[];
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
    message: string;
    i18nKey?: string;
}

export interface InvoiceOrderDetail {
    source_type: string;
    out_trade_no: string;
    amount: string;
    description: string;
    success_time: string;
    transaction_id: string;
    uuid: string;
    status: string;
    invoice_status: string;
    pdf_filename?: string | null;
}

interface InvoiceOrderDetailResponse {
    code: number;
    data: InvoiceOrderDetail;
    message: string;
    i18nKey?: string;
}

interface InvoiceApplyPayload {
    source_type: string;
    payment_out_trade_no: string;
    payment_amount: string;
    payment_description: string;
    payment_time: string;
    payment_transaction_id: string;
    title_type: string;
    invoice_type: string;
    invoice_title: string;
    tax_number: string;
    phone_number: string | null;
    email: string;
}

interface InvoiceApplyResponse {
    code: number;
    data: null;
    message: string;
    i18nKey?: string;
}

export interface PointsDetailItem {
    recordType: 'recharge' | 'consumption';
    pointsType: string;
    date: string;
    addAmount: number;
    reduceAmount: number;
    description: string;
    name: string
}

export interface PointsDetailResponse {
    success: boolean;
    code: number;
    data: {
        list: PointsDetailItem[];
        total: number;
        page: number;
        pageSize: number;
    };
}

export const getPointsDetail = async (page = 1, pageSize = 20): Promise<PointsDetailResponse | null> => {
    try {
        const base = process.env.REACT_APP_APPLICATION_MAIN_INTERFACE || '';
        const url = `${base}/api/member/points-detail?page=${page}&pageSize=${pageSize}`;
        const response = await requestGet(url, true) as PointsDetailResponse | null;
        if (response?.success && response?.code === 200) {
            return response;
        }
        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
};

export const createOrderApi = async (amount: number, description: string, timeExpire?: string, productId?: string): Promise<OrderResponse> => {
    try {
        return  await requestPost(
            `/payment-api/wechatpay/native`,
            {amount, description, timeExpire, productId}, true
        ) as OrderResponse;

    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const createWechatMembershipRenew = async (productId: string, immediateEffect?: boolean): Promise<MembershipRenewResponse> => {
    try {
        return await requestPost(
            `/payment-api/wechatpay/membership/renew`,
            { productId, immediateEffect },
            true
        ) as MembershipRenewResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

interface AlipayPageResponse {
    code: number;
    data: {
        html?: string;
        out_trade_no?: string;
        amount?: number;
        description?: string;
    };
    message: string;
    i18nKey?: string;
}

export const createAlipayPage = async (productId: string, description?: string): Promise<AlipayPageResponse> => {
    try {
        return await requestPost(
            `/payment-api/alipay/page`,
            { productId, description }, true
        ) as AlipayPageResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

interface MembershipAlipayRenewResponse {
    code: number;
    data: {
        out_trade_no?: string;
        html?: string;
        tier?: string;
        renewal?: boolean;
        amount?: number;
        description?: string;
    };
    message: string;
    i18nKey?: string;
}

export const createAlipayMembershipRenew = async (productId: string, immediateEffect?: boolean): Promise<MembershipAlipayRenewResponse> => {
    try {
        return await requestPost(
            `/payment-api/alipay/membership/renew`,
            { productId, immediateEffect },
            true
        ) as MembershipAlipayRenewResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const createAlipayForProductPage = async (productId: string): Promise<AlipayPageResponse> => {
    try {
        return await requestPost(
            `/payment-api/alipay/products/order`,
            { productId }, true
        ) as AlipayPageResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const queryAlipayOrder = async (out_trade_no: string): Promise<any> => {
    try {
        const response = await requestGet(
            `/payment-api/alipay/order/${out_trade_no}`, true
        );
        return response;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getQrcode = async (codeUrl: string): Promise<codeProps | null> => {
    try {
        const response = await requestPost(
            `/payment-api/wechatpay/qrcode`,
            {codeUrl}, true
        ) as QrCodeResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const sendGiftCode = async (giftCode: string): Promise< giftCodeProps | null> => {
    try {
        const response = await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/api/gift-code/redeem`,
            {giftCode}, true
        ) as giftResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getRecords = async (out_trade_no: string): Promise<orderStatusProps | null> => {
    try {
        const response = await requestGet(
            `/payment-api/wechatpay/order/${out_trade_no}`, true
        ) as orderStatusResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getRechargeRecords = async (out_trade_no: string): Promise< {records: rechargeStatusProps[]} | null> => {
    try {
        const response = await requestGet(
            `/payment-api/wechatpay/records?page=1&pageSize=10&out_trade_no=${out_trade_no}`, true
        ) as rechargeStatusResponse;
        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getProductRechargeRecords = async (out_trade_no: string): Promise< productRechargeStatusProps | null> => {
    try {
        const response = await requestGet(
            `/payment-api/wechatpay/points/status/${out_trade_no}`, true
        ) as productRechargeStatusResponse;
        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export interface MembershipQueueItem {
    id: number;
    user_uuid: string;
    tier: string;
    duration_months: number;
    status: 'active' | 'queued' | 'expired' | 'cancelled';
    points_distributed: number;
    source_order_no: string;
    payment_channel: string;
    amount: number;
    activated_at: string | null;
    expires_at: string | null;
    created_at: string;
    updated_at: string;
    tierDescription: string;
}

export interface MembershipQueueResponse {
    code: number;
    data: {
        queue: MembershipQueueItem[];
        stats: {
            queued: number;
            active: number;
            expired: number;
            cancelled: number;
        };
    };
    message: string;
}

export const getMembershipQueue = async (): Promise<MembershipQueueResponse['data'] | null> => {
    try {
        const response = await requestGet(`/payment-api/membership/queue`, true) as MembershipQueueResponse;

        if (response?.code === 200) {
            return response.data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getMemberInfo = async (email?: string): Promise<memberInfoProps | null> => {
    try {
        const response = await requestGet(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/api/member/info?userIdentifier=${email}`, true
        ) as memberResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getProductsList = async (pageSize?: number): Promise<ProductInfoProps | null> => {
    try {
        const response = await requestGet(
            `/payment-api/wechatpay/products?pageSize=${pageSize}`, true
        ) as productResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export interface WaterdropProductItem {
    id: number;
    product_id: string;
    status: string;
    product_group_key: string | null;
    previous_product_id: string | null;
    product_type: string;
    name: string;
    price: number;
    original_price: number;
    original_price_discount: string | null;
    membership_tier: string | null;
    duration_months: number | null;
    member_drops: number;
    points_type: string | null;
    points_amount: number;
    valid_days: number;
    invoice_enabled: number;
    description: string[] | null;
    introduce: string | null;
    sort_priority: number;
    sale_start_at: string | null;
    sold_out_at: string | null;
    sale_end_at: string | null;
    before_sale_display_mode: string;
    sold_out_display_mode: string;
    new_user_only: number;
    register_days_limit: number;
    allowed_member_tiers: string | null;
    purchase_limit_type: string;
    purchase_limit_count: number;
    queue_priority: number;
    created_at: string;
    updated_at: string;
    is_active: number
}

export type StorefrontProductItem = Pick<WaterdropProductItem,
    'id' |
    'product_id' |
    'product_group_key' |
    'product_type' |
    'name' |
    'price' |
    'original_price' |
    'original_price_discount' |
    'member_drops' |
    'points_type' |
    'points_amount' |
    'description' |
    'introduce' |
    'new_user_only' |
    'is_active'
>;

interface StorefrontProductsResponse {
    code: number;
    data: {
        total: number;
        page: number;
        pageSize: number;
        items: StorefrontProductItem[];
    };
    message: string;
}

export const getStorefrontProducts = async (): Promise<StorefrontProductItem[]> => {
    try {
        const query = new URLSearchParams({ status: 'active', view: 'storefront' });
        const response = await requestGet(
            `/payment-api/products?${query.toString()}`, true
        ) as StorefrontProductsResponse;

        if (response?.code === 200 && response.data?.items) {
            return response.data.items;
        }

        return [];
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const createProductOrder = async (productId: string, timeExpire: string): Promise<createProductsOrderResponse | null> => {
    try {
        return await requestPost(
            `/payment-api/wechatpay/products/order`,
            {productId, timeExpire}, true
        ) as createProductsOrderResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getInvoiceRecords = async (page = 1, pageSize = 999): Promise<InvoiceRecordsResponse["data"] | null> => {
    try {
        const response = await requestGet(
            `/payment-api/invoice/records?page=${page}&pageSize=${pageSize}`,
            true
        ) as InvoiceRecordsResponse;

        const { code, data } = response;
        if (code === 200) {
            return data;
        }
        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getInvoiceOrderDetail = async (outTradeNo: string): Promise<InvoiceOrderDetail | null> => {
    try {
        const response = await requestGet(
            `/payment-api/invoice/order-detail?out_trade_no=${encodeURIComponent(outTradeNo)}`,
            true
        ) as InvoiceOrderDetailResponse;

        const { code, data } = response;
        if (code === 200) {
            return data;
        }
        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const applyInvoice = async (payload: InvoiceApplyPayload): Promise<InvoiceApplyResponse> => {
    try {
        return await requestPost(
            `/payment-api/invoice/apply`,
            payload,
            true
        ) as InvoiceApplyResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}
